import { Button, Text } from '@/components/atoms';
import { BackButton, FormInput } from '@/components/molecules';
import { FC, useMemo, useRef } from 'react';
import {
  useForm,
  FormProvider as ReactHookFormProvider,
  SubmitHandler,
} from 'react-hook-form';
import { ConfirmationResult } from 'firebase/auth';
import phoneCodesJson from '@/utils/jsons/phoneCodes.json';
import { Stage } from '@/pages/auth/phone';
import useOnClickOrScrollOutside from '@/lib/hooks/useOnClickOrScrollOutside';
import { logError, logInfo, logDebug } from '@/utils/logger';
import { DropDownSelectBox } from '@/components';
import Image from 'next/image';

interface Props {
  onBackClick: () => void;
  onConfirm: (phoneNumber: string) => Promise<ConfirmationResult | undefined>;
  stage: Stage;
  setStage: (stage: Stage) => void;
  recaptchaLoading: boolean;
}

type PhoneFormFields = {
  country: string;
  phoneNumber: string;
};

const PhoneForm: FC<Props> = ({
  onBackClick,
  onConfirm,
  stage,
  setStage,
  recaptchaLoading,
}) => {
  const methods = useForm<PhoneFormFields>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      country: '',
      phoneNumber: '',
    },
  });

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
  } = methods;

  const formRef = useRef<HTMLDivElement | null>(null);

  useOnClickOrScrollOutside(formRef, () => {
    setTimeout(() => {
      clearErrors();
    }, 50);
  });

  const options = useMemo(() => {
    const _options = [];
    for (let key in phoneCodesJson) {
      const countryCode = key;
      const phoneCode = (phoneCodesJson as any)[key];
      const value = phoneCode.at(0) === '+' ? phoneCode : `+${phoneCode}`;

      const countryData = {
        label: `${countryCode} (${value})`,
        value,
        img: `https://flagsapi.com/${countryCode}/flat/64.png`,
      };
      _options.push(countryData);
    }
    return _options;
  }, []);

  const displayOptions = useMemo(() => {
    return options.map((option, index) => (
      <div key={`${index}-display-option`} className="flex items-center gap-2">
        <Image src={option.img} alt={option.label} width={24} height={24} />
        <span>{option.label}</span>
      </div>
    ));
  }, [options]);

  const selectedDisplayOptions = useMemo(() => {
    return options.map((option, index) => (
      <div
        key={`selected-${option.value}-${index}`}
        className="flex items-center gap-2"
      >
        <Image src={option.img} alt={option.label} width={24} height={24} />
        <span>{option.label}</span>
      </div>
    ));
  }, [options]);

  const handleOnSubmit: SubmitHandler<PhoneFormFields> = async ({
    country,
    phoneNumber,
  }) => {
    logDebug('handleOnSubmit triggered', 'handleOnSubmit');
    if ((window as any).recaptchaVerifier) {
      if (!country) {
        setError('country', {
          type: 'manual',
          message: 'Select a valid country',
        });
        return;
      }
      if (!phoneNumber.match(/^[0-9]+$/)) {
        setError('phoneNumber', {
          type: 'manual',
          message: 'Invalid phone number',
        });
        return;
      }
      const phoneString = `${country}${phoneNumber}`;
      logDebug(`Phone String: ${phoneString}`, 'handleOnSubmit');
      const confirmationResult = await onConfirm(phoneString);
      if (confirmationResult) {
        setStage(Stage.OTP);
      }
    } else {
      logError('reCAPTCHA verifier not found.', 'handleOnSubmit');
    }
  };

  return (
    <ReactHookFormProvider {...methods}>
      <div ref={formRef} className="flex flex-col gap-20 w-full">
        <div className="flex flex-col gap-md">
          <BackButton onBackClick={onBackClick} />
        </div>
        {stage === Stage.PHONE && (
          <div className="flex flex-col gap-10">
            <Text value="Enter your phone number" size="h1" />
            <div className="flex flex-col gap-md">
              <DropDownSelectBox
                name="country"
                label="Country Code"
                setValue={setValue}
                register={register}
                errors={errors}
                clearErrors={clearErrors}
                options={options.map((option) => ({
                  value: option.value,
                }))}
                displayOptions={displayOptions}
                selectedDisplayOptions={selectedDisplayOptions}
              />

              <FormInput
                name="phoneNumber"
                label="Phone"
                type="text"
                register={register}
                errors={errors}
                rules={{
                  required: 'Enter phone number to continue',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Invalid phone number',
                  },
                }}
              />
            </div>
            <div id="recaptcha-container" className="my-4"></div>
            <Button
              buttonId="submit-button"
              disabled={!!Object.keys(errors).length || recaptchaLoading}
              onClick={handleSubmit(handleOnSubmit)}
              fullWidth
            >
              {recaptchaLoading ? 'Loading...' : 'Continue'}
            </Button>
          </div>
        )}
      </div>
    </ReactHookFormProvider>
  );
};

export default PhoneForm;
